import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import DriverBrandHeader from './DriverBrandHeader';
import { getDriverLanguage, getDriverText } from './driverI18n';
import { getDriverTerms } from './driverTerms';

const DRIVER_PRIVACY_POLICY_URL = 'https://www.shonanpartner.com/driver-privacy/';

export default function DriverLegalCompliance({ driver }) {
  const navigate = useNavigate();
  const [activeLegalItem, setActiveLegalItem] = useState(null);
  const language = getDriverLanguage();
  const text = getDriverText(language);
  const terms = getDriverTerms(language);
  const legalItems = [
    {
      key: 'terms',
      title: text.termsOfUse,
      bodyTitle: terms.title,
      body: terms.text,
      testId: 'driver-legal-terms-button',
      iconPath: 'M7 3.5h7l3 3V20a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 20V5A1.5 1.5 0 0 1 7 3.5Z M14 3.5V7h3 M8.5 12h7 M8.5 15.5h5',
    },
    {
      key: 'privacy',
      title: text.privacyPolicy,
      bodyTitle: text.privacyPolicy,
      body: text.privacyPolicyBody,
      testId: 'driver-legal-privacy-button',
      iconPath: 'M12 3.5 18.5 6v5.2c0 4.1-2.7 7.8-6.5 9.3-3.8-1.5-6.5-5.2-6.5-9.3V6L12 3.5Z M9.8 12.1l1.4 1.4 3.2-3.4',
    },
    {
      key: 'location',
      title: text.locationInformation,
      bodyTitle: text.locationInformation,
      body: text.locationInformationBody,
      testId: 'driver-legal-location-button',
      iconPath: 'M12 21s6-5.3 6-11a6 6 0 1 0-12 0c0 5.7 6 11 6 11Z M12 12.2a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Z',
    },
    {
      key: 'notifications',
      title: text.notificationInformation,
      bodyTitle: text.notificationInformation,
      body: text.notificationInformationBody,
      testId: 'driver-legal-notification-button',
      iconPath: 'M18 10a6 6 0 1 0-12 0c0 5-2 5.5-2 7h16c0-1.5-2-2-2-7Z M9.8 20a2.3 2.3 0 0 0 4.4 0',
    },
    {
      key: 'safe-driving',
      title: text.safeDrivingGuide,
      bodyTitle: text.safeDrivingGuide,
      body: text.safeDrivingGuideBody,
      testId: 'driver-legal-safe-driving-button',
      iconPath: 'M5 14.5 7.2 8c.3-.9 1.1-1.5 2-1.5h5.6c.9 0 1.7.6 2 1.5l2.2 6.5 M6.5 14.5h11A2.5 2.5 0 0 1 20 17v2.5h-2.2L17 18H7l-.8 1.5H4V17a2.5 2.5 0 0 1 2.5-2.5Z M7.5 17h.1 M16.4 17h.1',
    },
    {
      key: 'emergency',
      title: text.emergencyResponse,
      bodyTitle: text.emergencyResponse,
      body: text.emergencyResponseBody,
      testId: 'driver-legal-emergency-button',
      iconPath: 'M12 3.5v17 M3.5 12h17 M6.2 6.2l11.6 11.6 M17.8 6.2 6.2 17.8',
    },
    {
      key: 'score-suspension',
      title: text.accountSuspensionScoreRules,
      bodyTitle: text.accountSuspensionScoreRules,
      body: text.accountSuspensionScoreRulesBody,
      testId: 'driver-legal-score-rules-button',
      iconPath: 'M7.5 20h9 M9 17h6 M12 3.5l2.1 4.2 4.6.7-3.3 3.2.8 4.6-4.2-2.2-4.2 2.2.8-4.6-3.3-3.2 4.6-.7L12 3.5Z',
    },
  ];

  return (
    <div className="driver-settings-page min-h-screen bg-surface" data-testid="driver-legal-compliance-page">
      <DriverBrandHeader pageTitle={text.legalComplianceCenter} driverName={driver?.name} onBack={() => navigate('/driver/orders')} />

      <div className="driver-settings-shell">
        <section className="driver-settings-panel driver-settings-legal-panel">
          <h2>{text.legalComplianceCenter}</h2>
          <p className="driver-settings-panel-hint">{text.legalComplianceCenterHint}</p>
          <div className="driver-legal-list">
            {legalItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveLegalItem(item)}
                className="driver-settings-list-row driver-legal-list-row"
                data-testid={item.testId}
              >
                <span className="driver-settings-row-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d={item.iconPath} stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span>{item.title}</span>
                <span className="driver-settings-chevron">›</span>
              </button>
            ))}
          </div>
        </section>
      </div>

      {activeLegalItem && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="premium-card w-full max-w-2xl max-h-[86vh] flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">{activeLegalItem.title}</h3>
              <button
                type="button"
                onClick={() => setActiveLegalItem(null)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={text.close}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
              <h4 className="font-bold text-base mb-4">{activeLegalItem.bodyTitle}</h4>
              <pre className="whitespace-pre-wrap text-sm leading-7 text-muted-foreground font-sans">
                {activeLegalItem.body}
              </pre>
              {activeLegalItem.key === 'privacy' && (
                <a
                  href={DRIVER_PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="premium-button mt-5 flex min-h-12 w-full items-center justify-center bg-primary px-4 py-3 text-center font-bold text-primary-foreground"
                  data-testid="driver-full-privacy-policy-link"
                >
                  {text.viewFullPrivacyPolicy}
                </a>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
